import { useMutation } from '@apollo/client';
import { ButtonDock } from '@focuslee/shared-mini-app';
import { Box, Stack } from '@mui/material';
import { Text } from '@telegram-apps/telegram-ui';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { updateGoalPlansMutation } from '../client/mutations/updateGoalPlans';
import { ListCategory } from '../client/types';
import { CommonIcon } from '../components/common/CommonIcon';
import { GoalsConfigurePageSkeleton } from '../components/goals/skeleton/GoalsConfigurePageSkeleton';
import { Page } from '../components/Page';
import { getColor } from '../helpers/color';
import { getLevelTargetXP } from '../helpers/goal';
import { mapTargetPlanToNumber, markNames, marks } from '../helpers/goals';
import { useCachedCategoriesWithFallback } from '../hooks/caching/category';
import { useCachedGoalsWithFallback } from '../hooks/caching/goal';
import { goalsSelectPath } from '../navigation/paths';

export const GoalsConfigurePage: FC = () => {
  const [updateGoalPlans, { loading: updateGoalPlansLoading }] = useMutation(updateGoalPlansMutation);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const byPass = searchParams.get('byPass');

  const { goals, loading: goalsLoading } = useCachedGoalsWithFallback();
  const { categories, loading: categoriesLoading } = useCachedCategoriesWithFallback();

  const loading = goalsLoading || categoriesLoading;

  const activeGoals = useMemo(() => goals.filter(g => g.active && g.enabled), [goals]);
  const categoryMap: Record<string, ListCategory> = useMemo(() => 
    Object.fromEntries(categories.map(c => [c.id, c])),
    [categories]
  );

  const handleProceed = useCallback(async () => {
    await updateGoalPlans({
      variables: {
        goalIdsToActivate: [],
        goalIdsToDeactivate: [],
        goalPlans: [],
      },
    });
    navigate(byPass ? -2 : -1);
  }, [updateGoalPlans, byPass]);

  const handleEdit = useCallback(() => {
    navigate(`${goalsSelectPath}?byPass=${byPass}`);
  }, [byPass]);

  useEffect(() => {
    if (!loading && activeGoals.length === 0) {
      handleEdit();
    }
  }, [loading, activeGoals.length, handleEdit]);

  if (loading) return <GoalsConfigurePageSkeleton />;

  return (
    <Page
      back
      insetEnabled={false}
      backClick={() => navigate(byPass ? -2 : -1)}
      footer={
        <ButtonDock
          secondary={{ text: 'Edit', onClick: handleEdit }}
          main={{ text: 'Proceed', onClick: handleProceed, loading: updateGoalPlansLoading }}
        />
      }
    >
      <Stack alignItems="center" justifyContent="center" minHeight="var(--tg-viewport-content-safe-area-inset-top)">
        <Text style={{ textAlign: 'center' }}>Previous goals</Text>
      </Stack>
      <Stack justifyContent="center" flex={1} direction="column" spacing={2} px={2} pt={2}>
        {activeGoals.map(g => {
          const targetXP = getLevelTargetXP(g);
          const planIndex = mapTargetPlanToNumber(g.targetPlan);
          const cat = categoryMap[g.categoryId];
          return (
            <Box key={g.id} display="flex" alignItems="center" sx={{ py: 1 }}>
              <Box sx={{
                  width: 20,
                  height: 20,
                  borderRadius: 1,
                  background: getColor(cat?.iconColor ?? ''),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
              }}>
                  <CommonIcon
                      icon={cat?.icon || 'emoji_events'}
                      type="filled"
                      size={14}
                      color="var(--tg-theme-text-color)"
                  />
              </Box>
              <Box sx={{ ml: 2, flex: 1 }}>
                <Text>{g.name}</Text>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Text style={{ color: 'var(--tgui--hint_color)', fontSize: '12px' }}>
                    {`Level ${g.targetLevel ?? 1} • ${targetXP} XP`}
                  </Text>
                  <Stack direction="row" justifyContent="flex-end" spacing={0.2}>
                    <CommonIcon icon={marks[planIndex - 1]} color="var(--tgui--hint_color)" type="filled" size={12} />
                    <Text style={{ color: 'var(--tgui--hint_color)', fontSize: '12px', lineHeight: '12px' }}>{markNames[mapTargetPlanToNumber(g.targetPlan ?? null) - 1]}</Text>
                  </Stack>
                </Stack>
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Page>
  );
}; 